import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProductFiscal, FiscalBatchData } from "@/hooks/useProductsFiscal";
import { NCM_SUGERIDOS, CFOP_OPTIONS, ORIGENS, UNIDADES_COMERCIAIS } from "@/lib/fiscal-constants";

interface TaxRule {
  id: string;
  name: string;
}

interface ProductFiscalEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: ProductFiscal | null;
  taxRules: TaxRule[];
  onSave: (productId: string, data: FiscalBatchData) => Promise<boolean>;
}

export function ProductFiscalEditDialog({
  open,
  onOpenChange,
  product,
  taxRules,
  onSave,
}: ProductFiscalEditDialogProps) {
  const [ncm, setNcm] = useState("");
  const [ncmManual, setNcmManual] = useState("");
  const [cest, setCest] = useState("");
  const [cfop, setCfop] = useState("");
  const [origem, setOrigem] = useState("");
  const [unidade, setUnidade] = useState("");
  const [taxRuleId, setTaxRuleId] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset form when product changes
  useEffect(() => {
    if (product) {
      const existingNcm = product.ncm || "";
      const isPresetNcm = NCM_SUGERIDOS.some((opt) => opt.value === existingNcm);
      
      setNcm(isPresetNcm ? existingNcm : "manual");
      setNcmManual(isPresetNcm ? "" : existingNcm);
      setCest(product.cest || "");
      setCfop(product.cfop || "");
      setOrigem(product.origem || "");
      setUnidade(product.unidade_comercial || "");
      setTaxRuleId(product.tax_rule_id || "");
    }
  }, [product]);

  const handleSave = async () => {
    if (!product) return;

    setSaving(true);
    try {
      const finalNcm = ncm === "manual" ? ncmManual : ncm;
      
      const data: FiscalBatchData = {
        ncm: finalNcm || null,
        cest: cest || null,
        cfop: cfop || null,
        origem: origem || null,
        unidade_comercial: unidade || null,
        tax_rule_id: taxRuleId || null,
      };

      const success = await onSave(product.id, data);
      if (success) {
        onOpenChange(false);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editar Dados Fiscais</DialogTitle>
          <DialogDescription className="font-medium text-foreground">
            {product.name}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* NCM */}
          <div className="space-y-2">
            <Label>NCM</Label>
            <Select value={ncm} onValueChange={setNcm}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o NCM" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Digitar manualmente</SelectItem>
                {NCM_SUGERIDOS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.value} - {opt.label.replace(`${opt.value} - `, "")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {ncm === "manual" && (
              <Input
                placeholder="Digite o NCM (8 dígitos)"
                value={ncmManual}
                onChange={(e) => setNcmManual(e.target.value.replace(/\D/g, "").slice(0, 8))}
                maxLength={8}
              />
            )}
          </div>

          {/* CEST */}
          <div className="space-y-2">
            <Label>CEST</Label>
            <Input
              placeholder="Código CEST (7 dígitos)"
              value={cest}
              onChange={(e) => setCest(e.target.value.replace(/\D/g, "").slice(0, 7))}
              maxLength={7}
            />
          </div>

          {/* CFOP */}
          <div className="space-y-2">
            <Label>CFOP</Label>
            <Select value={cfop} onValueChange={setCfop}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o CFOP" />
              </SelectTrigger>
              <SelectContent>
                {CFOP_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.value} - {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Origem */}
          <div className="space-y-2">
            <Label>Origem</Label>
            <Select value={origem} onValueChange={setOrigem}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a origem" />
              </SelectTrigger>
              <SelectContent>
                {ORIGENS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Unidade Comercial */}
          <div className="space-y-2">
            <Label>Unidade Comercial</Label>
            <Select value={unidade} onValueChange={setUnidade}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a unidade" />
              </SelectTrigger>
              <SelectContent>
                {UNIDADES_COMERCIAIS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Regra Tributária */}
          <div className="space-y-2">
            <Label>Regra Tributária</Label>
            <Select value={taxRuleId} onValueChange={setTaxRuleId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a regra" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Nenhuma</SelectItem>
                {taxRules.map((rule) => (
                  <SelectItem key={rule.id} value={rule.id}>
                    {rule.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
